'use client';

import { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, EyeOff, Trash2, Copy, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Plus, X } from 'lucide-react';
import { AddSectionMenu } from './AddSectionMenu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import { SECTION_METADATA, type Alignment, type DividerStyle, type BarcodeType } from '@/lib/templates/schema';
import type { ReceiptSection } from '@/lib/templates/schema';

const ALIGNMENT_OPTIONS: { value: Alignment; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const DIVIDER_OPTIONS: { value: DividerStyle; label: string; icon: string }[] = [
  { value: 'dashed', label: 'Dashed', icon: '---' },
  { value: 'equals', label: 'Equals', icon: '===' },
  { value: 'dots', label: 'Dots', icon: '...' },
  { value: 'colons', label: 'Colons', icon: ':::' },
  { value: 'stars', label: 'Stars', icon: '***' },
  { value: 'spaces', label: 'Blank', icon: '   ' },
];

const BARCODE_OPTIONS: { value: BarcodeType; label: string }[] = [
  { value: 'code128', label: 'Code 128' },
  { value: 'code39', label: 'Code 39' },
  { value: 'ean13', label: 'EAN-13' },
  { value: 'upc', label: 'UPC' },
  { value: 'qr', label: 'QR Code' },
];

const WHITESPACE_HINT_KEY = 'receipt-builder-whitespace-hint-dismissed';

// Whitespace hint component
function WhitespaceHint({ show, onDismiss }: { show: boolean; onDismiss: () => void }) {
  if (!show) return null;
  return (
    <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-1.5 rounded border border-dashed flex items-center justify-between gap-2 mb-3">
      <span>💡 Spaces are preserved for alignment</span>
      <button
        onClick={onDismiss}
        className="text-muted-foreground hover:text-foreground shrink-0"
        aria-label="Dismiss hint"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function InlineSectionEditor({ section }: { section: ReceiptSection }) {
  const { updateSection } = useReceiptBuilder();
  const [showWhitespaceHint, setShowWhitespaceHint] = useState(true);

  // Check localStorage on mount
  useEffect(() => {
    const dismissed = localStorage.getItem(WHITESPACE_HINT_KEY);
    if (dismissed === 'true') {
      setShowWhitespaceHint(false);
    }
  }, []);

  const dismissHint = () => {
    setShowWhitespaceHint(false);
    localStorage.setItem(WHITESPACE_HINT_KEY, 'true');
  };

  const update = (config: Record<string, unknown>) => {
    updateSection(section.id, config);
  };

  return (
    <div className="space-y-4 pt-3 border-t mt-3">
      {/* Header Section Editor */}
      {section.type === 'header' && (
        <>

          <div className="space-y-2">
            <Label className="text-xs">Logo (Upload or URL)</Label>
            <div className="flex gap-2">
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      update({ logoData: reader.result as string, logoUrl: '' });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="h-8 text-xs flex-1"
              />
              {(section.config.logoData || section.config.logoUrl) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => update({ logoData: '', logoUrl: '' })}
                >
                  Clear
                </Button>
              )}
            </div>
            <Input
              value={section.config.logoUrl || ''}
              onChange={(e) => update({ logoUrl: e.target.value, logoData: '' })}
              placeholder="Or paste URL..."
              className="h-8 text-xs"
            />
          </div>

          {(section.config.logoData || section.config.logoUrl) && (
            <div className="space-y-2">
              <Label className="text-xs">Logo Size</Label>
              <div>
                <span className="text-[10px] text-muted-foreground">Width: {section.config.logoWidth}px</span>
                <Slider
                  min={40}
                  max={200}
                  step={10}
                  value={[section.config.logoWidth]}
                  onValueChange={([value]) => update({ logoWidth: value })}
                />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Business Name</Label>
            <Input
              value={section.config.businessName}
              onChange={(e) => update({ businessName: e.target.value })}
              className="h-8 text-xs font-mono"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Address Lines</Label>
            <Textarea
              value={section.config.addressLines.join('\n')}
              onChange={(e) => update({ addressLines: e.target.value.split('\n') })}
              rows={3}
              className="text-xs font-mono"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Two Column Section Editor */}
      {section.type === 'two-column' && (
        <>
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          {/* Column 1 */}
          <div className="space-y-2 mb-4">
            <Label className="text-xs font-medium">Column 1</Label>
            {section.config.column1.map((item, index) => (
              <div key={item.id} className="flex gap-1 items-center">
                <Input
                  value={item.label}
                  onChange={(e) => {
                    const column1 = [...section.config.column1];
                    column1[index] = { ...item, label: e.target.value };
                    update({ column1 });
                  }}
                  placeholder="Label"
                  className="flex-1 h-7 text-xs font-mono"
                />
                <Input
                  value={item.value}
                  onChange={(e) => {
                    const column1 = [...section.config.column1];
                    column1[index] = { ...item, value: e.target.value };
                    update({ column1 });
                  }}
                  placeholder="Value"
                  className="flex-1 h-7 text-xs font-mono"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive shrink-0"
                  onClick={() => update({ column1: section.config.column1.filter((r) => r.id !== item.id) })}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => update({ column1: [...section.config.column1, { id: uuidv4(), label: 'Label', value: 'Value' }] })}
            >
              <Plus className="h-3 w-3 mr-1" /> Add line
            </Button>
          </div>

          {/* Column 2 */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Column 2</Label>
            {section.config.column2.map((item, index) => (
              <div key={item.id} className="flex gap-1 items-center">
                <Input
                  value={item.label}
                  onChange={(e) => {
                    const column2 = [...section.config.column2];
                    column2[index] = { ...item, label: e.target.value };
                    update({ column2 });
                  }}
                  placeholder="Label"
                  className="flex-1 h-7 text-xs font-mono"
                />
                <Input
                  value={item.value}
                  onChange={(e) => {
                    const column2 = [...section.config.column2];
                    column2[index] = { ...item, value: e.target.value };
                    update({ column2 });
                  }}
                  placeholder="Value"
                  className="flex-1 h-7 text-xs font-mono"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive shrink-0"
                  onClick={() => update({ column2: section.config.column2.filter((r) => r.id !== item.id) })}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => update({ column2: [...section.config.column2, { id: uuidv4(), label: 'Label', value: 'Value' }] })}
            >
              <Plus className="h-3 w-3 mr-1" /> Add line
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Items List Section Editor */}
      {section.type === 'items-list' && (
        <>
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <div className="flex items-center justify-between">
            <Label className="text-xs">Show Quantity Column</Label>
            <Switch
              checked={section.config.showQuantity}
              onCheckedChange={(checked) => update({ showQuantity: checked })}
            />
          </div>

          {section.config.showQuantity && (
            <div className="space-y-2">
              <Label className="text-xs">Quantity Position</Label>
              <div className="flex gap-1">
                <Button
                  variant={section.config.quantityPosition !== 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => update({ quantityPosition: 'left' })}
                  className="flex-1 h-7 text-xs"
                >
                  Left (Qty Item)
                </Button>
                <Button
                  variant={section.config.quantityPosition === 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => update({ quantityPosition: 'right' })}
                  className="flex-1 h-7 text-xs"
                >
                  Right (Item Qty)
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label className="text-xs">Show Header</Label>
            <Switch
              checked={section.config.showHeader}
              onCheckedChange={(checked) => update({ showHeader: checked })}
            />
          </div>

          {section.config.showHeader && (
            <>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Header Separator Line</Label>
                <Switch
                  checked={section.config.showHeaderDivider}
                  onCheckedChange={(checked) => update({ showHeaderDivider: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Header Labels</Label>
                <div className="flex gap-1">
                  {section.config.showQuantity && (
                    <Input
                      value={section.config.headerLabels?.quantity || ''}
                      onChange={(e) => update({ headerLabels: { ...section.config.headerLabels, quantity: e.target.value } })}
                      placeholder="Qty"
                      className="w-16 h-7 text-xs"
                    />
                  )}
                  <Input
                    value={section.config.headerLabels?.item || ''}
                    onChange={(e) => update({ headerLabels: { ...section.config.headerLabels, item: e.target.value } })}
                    placeholder="Item"
                    className="flex-1 h-7 text-xs"
                  />
                  <Input
                    value={section.config.headerLabels?.amount || ''}
                    onChange={(e) => update({ headerLabels: { ...section.config.headerLabels, amount: e.target.value } })}
                    placeholder="Amount"
                    className="w-20 h-7 text-xs"
                  />
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label className="text-xs">Items (drag to reorder)</Label>
            {section.config.items.map((item, index) => (
              <div key={item.id} className="flex gap-1 items-center">
                {/* Move up/down */}
                <div className="flex flex-col shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-3.5 w-5"
                    disabled={index === 0}
                    onClick={() => {
                      const items = [...section.config.items];
                      [items[index - 1], items[index]] = [items[index], items[index - 1]];
                      update({ items });
                    }}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-3.5 w-5"
                    disabled={index === section.config.items.length - 1}
                    onClick={() => {
                      const items = [...section.config.items];
                      [items[index], items[index + 1]] = [items[index + 1], items[index]];
                      update({ items });
                    }}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>
                {section.config.showQuantity && (
                  <Input
                    value={item.quantity || ''}
                    onChange={(e) => {
                      const items = [...section.config.items];
                      items[index] = { ...item, quantity: e.target.value };
                      update({ items });
                    }}
                    placeholder="Qty"
                    className="w-14 h-7 text-xs font-mono"
                  />
                )}
                <Input
                  value={item.name}
                  onChange={(e) => {
                    const items = [...section.config.items];
                    items[index] = { ...item, name: e.target.value };
                    update({ items });
                  }}
                  placeholder="Name"
                  className="flex-1 h-7 text-xs font-mono"
                />
                <Input
                  value={item.price}
                  onChange={(e) => {
                    const items = [...section.config.items];
                    items[index] = { ...item, price: e.target.value };
                    update({ items });
                  }}
                  placeholder="$0.00"
                  className="w-20 h-7 text-xs font-mono"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive shrink-0"
                  onClick={() => update({ items: section.config.items.filter((i) => i.id !== item.id) })}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => update({ items: [...section.config.items, { id: uuidv4(), quantity: '1', name: 'New Item', price: '$0.00' }] })}
            >
              <Plus className="h-3 w-3 mr-1" /> Add Item
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Totals Section Editor */}
      {section.type === 'totals' && (
        <>
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <div className="space-y-2">
            <Label className="text-xs">Label Position: {section.config.labelPosition ?? 50}%</Label>
            <Slider
              min={0}
              max={100}
              step={5}
              value={[section.config.labelPosition ?? 50]}
              onValueChange={([value]) => update({ labelPosition: value })}
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>← Left</span>
              <span>Right (overlap) →</span>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Label Alignment</Label>
            <div className="flex gap-1">
              {ALIGNMENT_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={(section.config.labelAlignment || 'left') === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => update({ labelAlignment: option.value })}
                  className="flex-1 h-7 text-xs"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Increase last line size</Label>
            <Switch
              checked={section.config.increaseTotalSize}
              onCheckedChange={(checked) => update({ increaseTotalSize: checked })}
            />
          </div>

          {section.config.increaseTotalSize && (
            <div className="flex gap-1">
              {[10, 20, 30, 40, 50].map((size) => (
                <Button
                  key={size}
                  variant={section.config.totalSizeIncrease === size ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => update({ totalSizeIncrease: size })}
                  className="flex-1 h-7 text-xs"
                >
                  +{size}%
                </Button>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs">Lines (drag to reorder)</Label>
            {section.config.lines.map((line, index) => (
              <div key={line.id} className="flex gap-1 items-center">
                {/* Move up/down */}
                <div className="flex flex-col shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-3.5 w-5"
                    disabled={index === 0}
                    onClick={() => {
                      const lines = [...section.config.lines];
                      [lines[index - 1], lines[index]] = [lines[index], lines[index - 1]];
                      update({ lines });
                    }}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-3.5 w-5"
                    disabled={index === section.config.lines.length - 1}
                    onClick={() => {
                      const lines = [...section.config.lines];
                      [lines[index], lines[index + 1]] = [lines[index + 1], lines[index]];
                      update({ lines });
                    }}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>
                <Input
                  value={line.label}
                  onChange={(e) => {
                    const lines = [...section.config.lines];
                    lines[index] = { ...line, label: e.target.value };
                    update({ lines });
                  }}
                  placeholder="Label"
                  className="flex-1 h-7 text-xs font-mono"
                />
                <Input
                  type="text"
                  value={line.value}
                  onChange={(e) => {
                    const lines = [...section.config.lines];
                    lines[index] = { ...line, value: e.target.value };
                    update({ lines });
                  }}
                  placeholder="0.00"
                  className="w-24 h-7 text-xs font-mono"
                />
                <Button
                  variant={line.isInverted ? 'default' : 'outline'}
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  title="Invert colors (black bg, white text)"
                  onClick={() => {
                    const lines = [...section.config.lines];
                    lines[index] = { ...line, isInverted: !line.isInverted };
                    update({ lines });
                  }}
                >
                  <span className="text-xs font-bold">▣</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive shrink-0"
                  onClick={() => update({ lines: section.config.lines.filter((l) => l.id !== line.id) })}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => update({ lines: [...section.config.lines, { id: uuidv4(), label: 'Label', value: '0.00', isBold: false, isLarge: false }] })}
            >
              <Plus className="h-3 w-3 mr-1" /> Add Line
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Payment Section Editor */}
      {section.type === 'payment' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs">Payment Type</Label>
            <div className="flex gap-1">
              <Button
                variant={section.config.paymentType === 'card' ? 'default' : 'outline'}
                size="sm"
                onClick={() => update({ paymentType: 'card' })}
                className="flex-1 h-7 text-xs"
              >
                💳 Card
              </Button>
              <Button
                variant={section.config.paymentType === 'cash' ? 'default' : 'outline'}
                size="sm"
                onClick={() => update({ paymentType: 'cash' })}
                className="flex-1 h-7 text-xs"
              >
                💵 Cash
              </Button>
            </div>
          </div>

          {/* Card Fields */}
          {section.config.paymentType === 'card' && (
            <div className="space-y-2">
              <Label className="text-xs">Card Fields</Label>
              {(section.config.cardFields || []).map((field, index) => (
                <div key={field.id} className="flex gap-1 items-center">
                  <div className="flex flex-col shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-3.5 w-5"
                      disabled={index === 0}
                      onClick={() => {
                        const cardFields = [...(section.config.cardFields || [])];
                        [cardFields[index - 1], cardFields[index]] = [cardFields[index], cardFields[index - 1]];
                        update({ cardFields });
                      }}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-3.5 w-5"
                      disabled={index === (section.config.cardFields || []).length - 1}
                      onClick={() => {
                        const cardFields = [...(section.config.cardFields || [])];
                        [cardFields[index], cardFields[index + 1]] = [cardFields[index + 1], cardFields[index]];
                        update({ cardFields });
                      }}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                  <Input
                    value={field.label}
                    onChange={(e) => {
                      const cardFields = [...(section.config.cardFields || [])];
                      cardFields[index] = { ...field, label: e.target.value };
                      update({ cardFields });
                    }}
                    placeholder="Label"
                    className="flex-1 h-7 text-xs font-mono"
                  />
                  <Input
                    value={field.value}
                    onChange={(e) => {
                      const cardFields = [...(section.config.cardFields || [])];
                      cardFields[index] = { ...field, value: e.target.value };
                      update({ cardFields });
                    }}
                    placeholder="Value"
                    className="flex-1 h-7 text-xs font-mono"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive shrink-0"
                    onClick={() => update({ cardFields: (section.config.cardFields || []).filter((f) => f.id !== field.id) })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-xs"
                onClick={() => update({ cardFields: [...(section.config.cardFields || []), { id: uuidv4(), label: 'Field', value: 'Value' }] })}
              >
                <Plus className="h-3 w-3 mr-1" /> Add Field
              </Button>
            </div>
          )}

          {/* Cash Fields */}
          {section.config.paymentType === 'cash' && (
            <div className="space-y-2">
              <Label className="text-xs">Cash Fields</Label>
              {(section.config.cashFields || []).map((field, index) => (
                <div key={field.id} className="flex gap-1 items-center">
                  <div className="flex flex-col shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-3.5 w-5"
                      disabled={index === 0}
                      onClick={() => {
                        const cashFields = [...(section.config.cashFields || [])];
                        [cashFields[index - 1], cashFields[index]] = [cashFields[index], cashFields[index - 1]];
                        update({ cashFields });
                      }}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-3.5 w-5"
                      disabled={index === (section.config.cashFields || []).length - 1}
                      onClick={() => {
                        const cashFields = [...(section.config.cashFields || [])];
                        [cashFields[index], cashFields[index + 1]] = [cashFields[index + 1], cashFields[index]];
                        update({ cashFields });
                      }}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                  <Input
                    value={field.label}
                    onChange={(e) => {
                      const cashFields = [...(section.config.cashFields || [])];
                      cashFields[index] = { ...field, label: e.target.value };
                      update({ cashFields });
                    }}
                    placeholder="Label"
                    className="flex-1 h-7 text-xs font-mono"
                  />
                  <Input
                    value={field.value}
                    onChange={(e) => {
                      const cashFields = [...(section.config.cashFields || [])];
                      cashFields[index] = { ...field, value: e.target.value };
                      update({ cashFields });
                    }}
                    placeholder="Value"
                    className="flex-1 h-7 text-xs font-mono"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive shrink-0"
                    onClick={() => update({ cashFields: (section.config.cashFields || []).filter((f) => f.id !== field.id) })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-xs"
                onClick={() => update({ cashFields: [...(section.config.cashFields || []), { id: uuidv4(), label: 'Field', value: 'Value' }] })}
              >
                <Plus className="h-3 w-3 mr-1" /> Add Field
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* DateTime Section Editor */}
      {section.type === 'datetime' && (
        <>
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <div className="space-y-2">
            <Label className="text-xs">Alignment</Label>
            <div className="flex gap-1">
              {ALIGNMENT_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={section.config.alignment === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => update({ alignment: option.value })}
                  className="flex-1 h-7 text-xs"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Date/Time Value</Label>
            <Input
              value={section.config.dateTime}
              onChange={(e) => update({ dateTime: e.target.value })}
              className="h-8 text-xs font-mono"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Custom Message Section Editor */}
      {section.type === 'custom-message' && (
        <>
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <div className="space-y-2">
            <Label className="text-xs">Alignment</Label>
            <div className="flex gap-1">
              {ALIGNMENT_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={section.config.alignment === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => update({ alignment: option.value })}
                  className="flex-1 h-7 text-xs"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Message</Label>
            <Textarea
              value={section.config.message}
              onChange={(e) => update({ message: e.target.value })}
              rows={4}
              className="text-xs font-mono"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Bold</Label>
            <Switch
              checked={section.config.isBold ?? false}
              onCheckedChange={(checked) => update({ isBold: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Barcode Section Editor */}
      {section.type === 'barcode' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select
                value={section.config.type}
                onValueChange={(value: BarcodeType) => {
                  if (value === 'qr') {
                    update({ type: value, value: 'QR_MEDIUM', showValue: false, height: 100, width: 2 });
                  } else {
                    // When switching from QR to other types, reset to default barcode value and size
                    const currentValue = section.config.value;
                    const isQRValue = currentValue?.startsWith('QR_');
                    const wasQR = section.config.type === 'qr';
                    if (isQRValue || wasQR) {
                      update({ type: value, value: '123456789012', height: 50, width: 2 });
                    } else {
                      update({ type: value });
                    }
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BARCODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-xs">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {section.config.type === 'qr' ? (
              <div className="space-y-1">
                <Label className="text-xs">Detail Level</Label>
                <Select
                  value={section.config.value?.startsWith('QR_') ? section.config.value : 'QR_MEDIUM'}
                  onValueChange={(value) => update({ value })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="QR_SIMPLE" className="text-xs">Simple</SelectItem>
                    <SelectItem value="QR_MEDIUM" className="text-xs">Medium</SelectItem>
                    <SelectItem value="QR_DETAILED" className="text-xs">Detailed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1">
                <Label className="text-xs">Value</Label>
                <Input
                  value={section.config.value}
                  onChange={(e) => update({ value: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            )}
          </div>

          {section.config.type !== 'qr' && (
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show Value Below</Label>
              <Switch
                checked={section.config.showValue}
                onCheckedChange={(checked) => update({ showValue: checked })}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Size</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-[10px] text-muted-foreground">{section.config.type === 'qr' ? 'QR Size' : 'Width'}</span>
                <Slider
                  min={section.config.type === 'qr' ? 60 : 0.5}
                  max={section.config.type === 'qr' ? 150 : 3}
                  step={section.config.type === 'qr' ? 10 : 0.1}
                  value={[section.config.type === 'qr' ? section.config.height : section.config.width]}
                  onValueChange={([value]) => update(section.config.type === 'qr' ? { height: value } : { width: value })}
                />
              </div>
              {section.config.type !== 'qr' && (
                <div>
                  <span className="text-[10px] text-muted-foreground">Height</span>
                  <Slider
                    min={15}
                    max={100}
                    step={5}
                    value={[section.config.height]}
                    onValueChange={([value]) => update({ height: value })}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Divider at bottom</Label>
            <Switch
              checked={section.config.showDivider}
              onCheckedChange={(checked) => update({ showDivider: checked })}
            />
          </div>
          {section.config.showDivider && (
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ dividerStyle: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.dividerStyle === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-[10px]">{option.icon}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Divider Section Editor */}
      {section.type === 'divider' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs">Style</Label>
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update({ style: option.value })}
                  className={`flex-1 h-7 px-1 rounded border flex items-center justify-center transition-colors ${
                    section.config.style === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-xs">{option.icon}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Spacing (px): {section.config.spacing}</Label>
            <Slider
              min={4}
              max={24}
              step={2}
              value={[section.config.spacing]}
              onValueChange={([value]) => update({ spacing: value })}
            />
          </div>
        </>
      )}
    </div>
  );
}

interface SortableItemProps {
  section: ReceiptSection;
  isExpanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggleExpand: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function SortableItem({
  section,
  isExpanded,
  isFirst,
  isLast,
  onToggleExpand,
  onToggle,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = SECTION_METADATA.find((m) => m.type === section.type);

  const headerRef = useRef<HTMLDivElement>(null);

  // Scroll the section header into view when it becomes expanded (e.g. from canvas click)
  useEffect(() => {
    if (isExpanded && headerRef.current) {
      // Small delay so the collapsible content renders and layout settles
      const timer = setTimeout(() => {
        headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        rounded-lg border transition-colors
        ${!section.enabled
          ? 'opacity-60 bg-muted/40 border-dashed border-muted-foreground/30'
          : `bg-card ${isExpanded ? 'border-green-400/70 shadow-[0_0_8px_rgba(74,222,128,0.25)]' : 'border-border hover:border-primary/50'}`
        }
      `}
    >
      <Collapsible open={isExpanded} onOpenChange={onToggleExpand}>
        <div
          ref={headerRef}
          className="flex items-center gap-2 p-3 cursor-pointer select-none scroll-mt-4"
          onClick={onToggleExpand}
        >
          {/* Drag handle - desktop only */}
          <div
            className="hidden md:flex cursor-grab active:cursor-grabbing p-3 -ml-1 hover:bg-muted rounded-lg touch-manipulation"
            onClick={(e) => e.stopPropagation()}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>

          {/* Move up/down buttons - mobile only */}
          <div className="flex md:hidden flex-col -ml-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={isFirst}
              onClick={onMoveUp}
              title="Move up"
            >
              <ArrowUp className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={isLast}
              onClick={onMoveDown}
              title="Move down"
            >
              <ArrowDown className="h-5 w-5" />
            </Button>
          </div>

          <span className={`text-lg ${!section.enabled ? 'grayscale opacity-50' : ''}`}>{meta?.icon || '📄'}</span>

          <div className="flex-1 min-w-0 text-left">
            <div className={`font-medium text-sm truncate ${!section.enabled ? 'text-muted-foreground line-through decoration-muted-foreground/40' : ''}`}>
              {meta?.label || section.type}
            </div>
          </div>

          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onToggle}
              title={section.enabled ? 'Hide section' : 'Show section'}
            >
              {section.enabled ? (
                <Eye className="h-3.5 w-3.5" />
              ) : (
                <EyeOff className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onDuplicate}
              title="Duplicate section"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={onDelete}
              title="Delete section"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onToggleExpand}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <CollapsibleContent>
          <div className="px-3 pb-3">
            <InlineSectionEditor section={section} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function SectionList() {
  const {
    template,
    selectedSectionId,
    selectSection,
    toggleSection,
    removeSection,
    duplicateSection,
    moveSection,
  } = useReceiptBuilder();

  const expandedId = selectedSectionId;
  const setExpandedId = (id: string | null) => selectSection(id);

  // Auto-open the first section on initial mount only.
  // Template switches are handled by setTemplate/resetTemplate in the store.
  useEffect(() => {
    if (!expandedId && template.sections.length > 0) {
      selectSection(template.sections[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleDeleteConfirm = () => {
    if (pendingDeleteId) {
      removeSection(pendingDeleteId);
      if (expandedId === pendingDeleteId) selectSection(null);
      setPendingDeleteId(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = template.sections.findIndex((s) => s.id === active.id);
      const newIndex = template.sections.findIndex((s) => s.id === over.id);
      moveSection(oldIndex, newIndex);
    }
  };

  if (template.sections.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No sections yet.</p>
          <p className="text-xs mt-1">Click below to add your first section.</p>
        </div>
        <AddSectionMenu />
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-20">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={template.sections.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {template.sections.map((section, index) => (
              <SortableItem
                key={section.id}
                section={section}
                isExpanded={expandedId === section.id}
                isFirst={index === 0}
                isLast={index === template.sections.length - 1}
                onToggleExpand={() => setExpandedId(expandedId === section.id ? null : section.id)}
                onToggle={() => toggleSection(section.id)}
                onDelete={() => setPendingDeleteId(section.id)}
                onDuplicate={() => duplicateSection(section.id)}
                onMoveUp={() => moveSection(index, index - 1)}
                onMoveDown={() => moveSection(index, index + 1)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      
      {/* Add Section Button - Always at the bottom */}
      <AddSectionMenu />

      <AlertDialog open={!!pendingDeleteId} onOpenChange={(open) => { if (!open) setPendingDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete section?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this section? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
